/**
 * Push handler — consolidates pending sync events and pushes
 * changed fields to the server. Handles conflicts via three-way merge.
 */

import { db } from '../database/schema.js'
import type { ContextSyncApi, PushResult } from './context-sync-api.js'
import { consolidateSyncEvents } from './consolidation.js'
import { getBaseSnapshot, clearBaseSnapshot } from './base-snapshot.js'
import { threeWayMerge } from './three-way-merge.js'
import { applyServerChange } from './server-apply.js'
import { authService } from '../services/auth/auth-service.js'
import { logger } from '../utils/logger.js'

const log = logger.create('sync-push')

export interface PushSyncResult {
  success: boolean
  pushed: number
  conflicts: PushConflict[]
  denied: PushDenial[]
  errors: string[]
}

export interface PushConflict {
  entityType: string
  entityId: string
  autoResolved: boolean
  conflictFields?: string[]
}

export interface PushDenial {
  entityType: string
  entityId: string
  reason: string
}

const ENTITY_TABLES: Record<string, string> = {
  route: 'routes',
  plan: 'plans',
  activity: 'activities',
  feature: 'features',
  layerGroup: 'layerGroups',
  edge: 'edges',
  team: 'teams',
  user: 'users',
}

async function getEntityOwner(entityType: string, entityId: string): Promise<string | undefined> {
  if (entityType === 'edge') {
    const edge = await db.edges.get(entityId)
    if (!edge) return undefined
    const subject = (edge as unknown as Record<string, unknown>).subject as string | undefined
    if (!subject?.includes(':')) return undefined
    const [subType, subId] = [
      subject.slice(0, subject.indexOf(':')),
      subject.slice(subject.indexOf(':') + 1),
    ]
    return getEntityOwner(subType, subId)
  }

  if (entityType === 'user') return entityId

  const tableName = ENTITY_TABLES[entityType]
  if (!tableName) return undefined
  const table = (db as unknown as Record<string, import('dexie').Table>)[tableName]
  if (!table) return undefined
  const entity = await table.get(entityId)
  return (entity as Record<string, unknown> | undefined)?.createdBy as string | undefined
}

async function filterChangesForUser(
  changes: import('./consolidation.js').ConsolidatedChange[],
  userId: string
): Promise<import('./consolidation.js').ConsolidatedChange[]> {
  const filtered: import('./consolidation.js').ConsolidatedChange[] = []
  for (const change of changes) {
    const owner = await getEntityOwner(change.entityType, change.entityId)
    if (owner === undefined || owner === userId) {
      filtered.push(change)
    } else {
      log.debug(
        `Skipping sync for ${change.entityType}:${change.entityId} (owned by ${owner}, current user ${userId})`
      )
    }
  }
  return filtered
}

export async function pushPendingChanges(api: ContextSyncApi): Promise<PushSyncResult> {
  const result: PushSyncResult = {
    success: true,
    pushed: 0,
    conflicts: [],
    denied: [],
    errors: [],
  }

  try {
    const allChanges = await consolidateSyncEvents()
    if (allChanges.length === 0) return result

    const currentUserId = authService.getCurrentUserId()
    const changes = await filterChangesForUser(allChanges, currentUserId)
    if (changes.length === 0) return result

    const response = await api.push({
      changes: changes.map(c => ({
        syncEventId: c.syncEventIds[0],
        entityType: c.entityType,
        entityId: c.entityId,
        operation: c.operation,
        data: c.data,
        expectedVersion: c.expectedVersion,
      })),
    })

    for (let i = 0; i < response.results.length; i++) {
      const pushResult = response.results[i]
      const change = changes[i]

      if (pushResult.success) {
        await handleSuccess(change.entityType, change.entityId, pushResult, change.syncEventIds)
        result.pushed++
        continue
      }

      if (pushResult.denied) {
        result.denied.push({
          entityType: change.entityType,
          entityId: change.entityId,
          reason: pushResult.denied.reason,
        })
        continue
      }

      if (pushResult.conflict) {
        const conflictResult = await handleConflict(
          change.entityType,
          change.entityId,
          pushResult,
          api,
          change.syncEventIds
        )
        result.conflicts.push(conflictResult)

        if (conflictResult.autoResolved) {
          result.pushed++
        }
        continue
      }

      result.errors.push(
        pushResult.error ?? `Unknown error for ${change.entityType}:${change.entityId}`
      )
    }
  } catch (err) {
    result.success = false
    result.errors.push(err instanceof Error ? err.message : String(err))
  }

  return result
}

async function handleSuccess(
  entityType: string,
  entityId: string,
  pushResult: PushResult,
  syncEventIds: string[]
): Promise<void> {
  if (pushResult.newVersion != null) {
    await updateLocalVersion(entityType, entityId, pushResult.newVersion)
  }

  await markSynced(syncEventIds)
  await clearBaseSnapshot(entityType, entityId)
}

async function handleConflict(
  entityType: string,
  entityId: string,
  pushResult: PushResult,
  api: ContextSyncApi,
  syncEventIds: string[]
): Promise<PushConflict> {
  const serverData = pushResult.conflict!.serverData
  const base = await getBaseSnapshot(entityType, entityId)

  if (!base) {
    return {
      entityType,
      entityId,
      autoResolved: false,
      conflictFields: ['unknown'],
    }
  }

  const local = await getLocalEntityData(entityType, entityId)
  if (!local) {
    return { entityType, entityId, autoResolved: false }
  }

  const mergeResult = threeWayMerge(base, local, serverData)

  if (mergeResult.autoResolved) {
    await applyServerChange(async () => {
      await updateLocalEntity(entityType, entityId, mergeResult.merged)
    })

    const retryResponse = await api.push({
      changes: [
        {
          syncEventId: syncEventIds[0],
          entityType,
          entityId,
          operation: 'update',
          data: mergeResult.merged,
          expectedVersion: pushResult.conflict!.serverVersion,
        },
      ],
    })

    if (retryResponse.results[0]?.success) {
      const newVersion = retryResponse.results[0].newVersion
      if (newVersion != null) {
        await updateLocalVersion(entityType, entityId, newVersion)
      }
      await markSynced(syncEventIds)
      await clearBaseSnapshot(entityType, entityId)
      return { entityType, entityId, autoResolved: true }
    }
  }

  await applyServerChange(async () => {
    await updateLocalEntity(entityType, entityId, serverData)
  })

  return {
    entityType,
    entityId,
    autoResolved: false,
    conflictFields: mergeResult.conflicts.map(c => c.field),
  }
}

async function updateLocalVersion(
  entityType: string,
  entityId: string,
  newVersion: number
): Promise<void> {
  const tableName = ENTITY_TABLES[entityType]
  if (!tableName) return
  const table = (db as unknown as Record<string, import('dexie').Table>)[tableName]
  if (!table) return
  await table.update(entityId, { version: newVersion })
}

async function updateLocalEntity(
  entityType: string,
  entityId: string,
  data: Record<string, unknown>
): Promise<void> {
  const tableName = ENTITY_TABLES[entityType]
  if (!tableName) return
  const table = (db as unknown as Record<string, import('dexie').Table>)[tableName]
  if (!table) return
  await table.update(entityId, { ...data, id: entityId })
}

async function getLocalEntityData(
  entityType: string,
  entityId: string
): Promise<Record<string, unknown> | undefined> {
  const tableName = ENTITY_TABLES[entityType]
  if (!tableName) return undefined
  const table = (db as unknown as Record<string, import('dexie').Table>)[tableName]
  if (!table) return undefined
  const entity = await table.get(entityId)
  return entity ? { ...(entity as Record<string, unknown>) } : undefined
}

async function markSynced(syncEventIds: string[]): Promise<void> {
  const now = new Date().toISOString()
  for (const id of syncEventIds) {
    await db.syncEvents.update(id, { syncedAt: now })
  }
}
